import React, { useEffect, useState } from 'react';
import { Attendance } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { UserService } from '../../services/user.service';
import { UserType } from '../../interfaces/userType.enum';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, CircularProgress, IconButton, Tooltip, TablePagination
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import DownloadIcon from '@mui/icons-material/Download';
import { Edit } from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import { InstanceStatus, isInstanceStatus } from '../shifts/instanceStatus';

interface User {
    id: string;
    username: string;
    type: UserType;
}

interface UserAttendanceRow {
    user: User;
    attendance: Attendance | undefined;
}

const InstitutionTodayAttendance: React.FC = () => {
    const [rows, setRows] = useState<UserAttendanceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportStartDate, setExportStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [exportEndDate, setExportEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [exporting, setExporting] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    const [saving, setSaving] = useState(false);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const toast = useToast();
    const { t } = useTranslation(['attendance', 'common', 'shifts']);
    const { shifts } = useShiftLookup();

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersResponse, todayAttendances] = await Promise.all([
                UserService.getInstitutionUsers(0, 200),
                AttendanceService.getInstitutionTodayAttendance(),
            ]);
            const users: User[] = usersResponse.content ?? usersResponse;
            const merged: UserAttendanceRow[] = users.map(user => ({
                user,
                attendance: todayAttendances.find(a => a.userId === user.id),
            }));
            setRows(merged);
        } catch (err) {
            toast.showApiError(err, t('todayAttendance.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatTime = (dateString: string) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'HH:mm');
    };

    const handleOpenEditDialog = (attendance: Attendance) => {
        setEditingAttendance(attendance);
        setEditStartTime(format(parseISO(attendance.startTime), 'HH:mm'));
        setEditEndTime(format(parseISO(attendance.endTime), 'HH:mm'));
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingAttendance(null);
    };

    const handleSaveEdit = async () => {
        if (!editingAttendance) return;
        try {
            setSaving(true);
            const date = editingAttendance.date;
            const startTime = new Date(`${date}T${editStartTime}:00`).toISOString();
            const endTime = new Date(`${date}T${editEndTime}:00`).toISOString();
            await AttendanceService.updateAttendance(editingAttendance.id, startTime, endTime);
            toast.showSuccess(t('updateSuccess'));
            handleCloseEditDialog();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('updateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            await AttendanceService.downloadMonthlyReport(
                new Date(exportStartDate),
                new Date(exportEndDate)
            );
            setExportDialogOpen(false);
        } catch (err) {
            toast.showApiError(err, t('todayAttendance.downloadFailed'));
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <Typography>{t('loading')}</Typography>;

    const filteredRows = rows.filter(({ attendance }) =>
        !selectedShiftId || attendance?.shiftId === selectedShiftId);
    const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">
                        {t('todayAttendance.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {format(new Date(), 'dd-MM-yyyy')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel id="shift-filter-label">{t('shiftFilter.label')}</InputLabel>
                        <Select
                            labelId="shift-filter-label"
                            value={selectedShiftId}
                            label={t('shiftFilter.label')}
                            onChange={(e) => { setSelectedShiftId(e.target.value); setPage(0); }}
                            size="small"
                        >
                            <MenuItem value="">{t('shiftFilter.all')}</MenuItem>
                            {shifts.filter(s => s.isActive).map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.code} – {s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => setExportDialogOpen(true)}
                    >
                        {t('todayAttendance.exportButton')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={tableHeaderStyle}>{t('todayAttendance.columns.username')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('todayAttendance.columns.checkIn')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('todayAttendance.columns.checkOut')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('common:fields.status')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.shift')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.instanceStatus')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.late')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.ot')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('common:fields.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows
                            .map(({ user, attendance }) => {
                                const isPresent = !!attendance;
                                return (
                                    <TableRow key={user.id}>
                                        <TableCell sx={tableCellStyle}>{displayUsername(user.username)}</TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance ? formatTime(attendance.startTime) : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance ? formatTime(attendance.endTime) : '-'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                ...tableCellStyle,
                                                backgroundColor: isPresent ? '#e6ffe6' : '#ffebeb',
                                                color: isPresent ? '#2e7d32' : '#c62828',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {isPresent ? t('dayStatus.present') : t('dayStatus.absent')}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.shiftCode
                                                ? <Chip label={attendance.shiftCode} size="small" />
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {isInstanceStatus(attendance?.instanceStatus)
                                                ? t(`shifts:instanceStatus.${attendance!.instanceStatus as InstanceStatus}`)
                                                : attendance?.instanceStatus ?? '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.isLate
                                                ? <Chip label={t('minutesShort', { minutes: attendance.lateMinutes })} size="small" color="error" />
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.overtimeMinutes && attendance.overtimeMinutes > 0
                                                ? <Chip label={t('minutesShort', { minutes: attendance.overtimeMinutes })} size="small" />
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance && (
                                                <Tooltip title={t('editAttendance')} arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDialog(attendance)}
                                                        color="primary"
                                                        aria-label={t('editAttendance')}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t('editAttendance')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label={t('common:fields.date')}
                            value={format(new Date(), 'dd-MM-yyyy')}
                            disabled
                            fullWidth
                        />
                        <TextField
                            label={t('columns.startTime')}
                            type="time"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label={t('columns.endTime')}
                            type="time"
                            value={editEndTime}
                            onChange={(e) => setEditEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog} disabled={saving}>{t('common:actions.cancel')}</Button>
                    <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
                        {saving ? t('saving') : t('common:actions.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
                <DialogTitle>{t('todayAttendance.exportTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, marginTop: 1 }}>
                        <TextField
                            label={t('startDate')}
                            type="date"
                            value={exportStartDate}
                            onChange={(e) => setExportStartDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            label={t('endDate')}
                            type="date"
                            value={exportEndDate}
                            onChange={(e) => setExportEndDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
                        {t('common:actions.cancel')}
                    </Button>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        disabled={exporting}
                        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                        {exporting ? t('downloading') : t('common:actions.download')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default InstitutionTodayAttendance;
